import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Dialog } from "@mui/material";
import Label from "../../../../components/form_fields/Label";
import OrangeButton from "../../../../components/buttons/OrangeButton";
import FormDescriptionInput from "../../../../components/form_fields/FormDescriptionInput";
import FormCodeInput from "../../../../components/form_fields/FormCodeInput";
import {
	useCreateCustomTaxReliefGroup,
	useUpdateCustomTaxReliefGroup,
} from "../../../../services/custom-tax-relief-group.service";
import { useManageTableWithSearchParamsData } from "../../../../helpers/useManageTable";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import { CustomTaxReliefGroupDto } from "../../../../models/custom-tax-relief-group.dto";

interface IFormInputs {
	name: string;
	code: string;
	description: string;
	active: boolean;
}

interface ICustomTaxReliefGroupModalForm {
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	title: string;
	isEditForm: boolean;
	tableAPIRef: React.MutableRefObject<any>;
	reloadTableData: () => void;
}

const CustomTaxReliefGroupModalForm = ({
	open,
	setOpen,
	title,
	isEditForm,
	tableAPIRef,
	reloadTableData,
}: ICustomTaxReliefGroupModalForm) => {
	const { currentCompany } = useCompanyStore();
	const { formDataValues } = useEditFormStore();
	const [uploading, setUploading] = useState(false);

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<IFormInputs>({
		defaultValues: isEditForm
			? {
					name: formDataValues?.name,
					code: formDataValues?.code,
					description: formDataValues?.description,
					active: formDataValues?.active,
				}
			: undefined,
	});

	// Reset form when modal opens/closes or mode changes
	useEffect(() => {
		if (open) {
			reset(
				isEditForm
					? {
							name: formDataValues?.name,
							code: formDataValues?.code,
							description: formDataValues?.description,
							active: formDataValues?.active,
						}
					: { name: "", code: "", description: "", active: true },
			);
		}
	}, [open, isEditForm, formDataValues, reset]);

	const { mutate: createGroup } = useCreateCustomTaxReliefGroup();
	const { mutate: updateGroup } = useUpdateCustomTaxReliefGroup();

	const onSubmit: SubmitHandler<IFormInputs> = (data) => {
		setUploading(true);

		if (isEditForm) {
			updateGroup(
				{
					payload: data,
					companyId:
						formDataValues?.companyId ?? (currentCompany?.id as number),
					id: formDataValues?.id,
				},
				{
					onSuccess: () => {
						reloadTableData();
						setOpen(false);
					},
					onSettled: () => setUploading(false),
				},
			);
		} else {
			createGroup(
				{
					companyId: currentCompany?.id as number,
					payload: data,
				},
				{
					onSuccess: () => {
						reloadTableData();
						setOpen(false);
					},
					onSettled: () => setUploading(false),
				},
			);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={() => setOpen(false)}
			PaperProps={{ className: "rounded-xl" }}
			maxWidth="sm"
			fullWidth
		>
			<div className="px-8 pt-8 pb-6">
				<h2 className="font-semibold text-lg mb-6">{title}</h2>

				<form onSubmit={handleSubmit(onSubmit)}>
					<div className="grid grid-cols-2 gap-x-4 gap-y-6">
						{/* Name */}
						<div className="w-full">
							<Label title="Name" for="name" />
							<input
								placeholder="Enter Name"
								type="text"
								{...register("name", { required: "Enter name here" })}
								id="name"
								className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
									errors.name
										? "border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`}
							/>
							{errors.name && (
								<p className="text-red-500 text-sm mt-1">
									{errors.name.message}
								</p>
							)}
						</div>

						{/* Code */}
						<div className="w-full">
							<Label title="Unique Code" for="code" />
							<FormCodeInput
								register={register}
								attributes={{
									className: `w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
										errors.code
											? "border border-red-500 focus:border-red-500 focus:outline-red-500"
											: "border-none"
									}`,
								}}
							/>
							{errors.code && (
								<p className="text-red-500 text-sm mt-1">
									{errors.code.message}
								</p>
							)}
						</div>

						{/* Description */}
						<div className="col-span-2 w-full">
							<Label title="Description" for="description" />
							<FormDescriptionInput
								attributes={{
									className: `w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
										errors.description
											? "border border-red-500 focus:border-red-500 focus:outline-red-500"
											: "border-none"
									}`,
								}}
								register={register}
								required="Enter brief description here"
							/>
							{errors.description && (
								<p className="text-red-500 text-sm mt-1">
									{errors.description.message}
								</p>
							)}
						</div>

						{/* Active */}
						<div className="col-span-2 flex space-x-3 items-center">
							<input
								type="checkbox"
								{...register("active")}
								id="active"
								defaultChecked={isEditForm ? formDataValues?.active : true}
							/>
							<label htmlFor="active">Active</label>
						</div>
					</div>

					<div className="w-full flex items-center justify-between mt-8">
						<button
							type="button"
							onClick={() => setOpen(false)}
							className="px-10 py-4 border text-gray-400 hover:text-gray-500 border-gray-300 rounded-lg text-2xs"
						>
							Cancel
						</button>
						<OrangeButton
							title={uploading ? "Saving..." : title}
							type="submit"
							className="h-14 px-8"
							disabled={uploading}
						/>
					</div>
				</form>
			</div>
		</Dialog>
	);
};

export default CustomTaxReliefGroupModalForm;
